import { useNavigate } from "@tanstack/react-router";
import { useCallback, type FC } from "react";
import { useBasePath } from "../../../hooks/router";
import { useTRPC } from "../../../trpc";
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@tanstack/react-query";

interface DeleteUserModalProps {
  open: boolean;
  onClose: () => void;
}

export const DeleteUserModal: FC<DeleteUserModalProps> = ({ onClose, open }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const pathname = useBasePath();

  const { mutateAsync: deleteUser, isPending: isPendingDelete, error } = useMutation(trpc.deleteUser.mutationOptions());

  const handleDelete = useCallback(() => {
    void deleteUser().then(() => {
      void navigate({ to: `${pathname}/sign-in` });
    });
  }, [deleteUser, navigate]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={intl.formatMessage({
        defaultMessage: "Delete user",
        id: "title.deleteUser",
      })}
    >
      <Stack gap="xs">
        <Text size="sm">
          <FormattedMessage
            defaultMessage="Are you sure you want to delete your user? User would lose access to all associated accounts. All active sessions will be terminated"
            id="text.deleteUser"
          />
        </Text>
        {error
          && (
            <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
              {error.message}
            </Alert>
          )}
        <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
          <FormattedMessage
            defaultMessage="Deletion is permanent and the associated email address cannot be used to create a new account."
            id="text.deleteUserConsequences"
          />
        </Alert>
      </Stack>
      <Group gap="xs" justify="flex-end" pt="lg">
        <Button variant="secondary" type="reset" loading={isPendingDelete} onClick={onClose}>
          <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
        </Button>
        <Button variant="danger" type="submit" loading={isPendingDelete} onClick={handleDelete}>
          <FormattedMessage defaultMessage="Delete User" id="button.deleteUser" />
        </Button>
      </Group>
    </Modal>
  );
};
